import React, { useState, useEffect } from 'react';
import { Save, X, CreditCard, Settings, Info, Building2 } from 'lucide-react';
import Button from '../ui/Button';
import Card from '../ui/Card';
import toast from 'react-hot-toast';
import { dataHelpers } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const MonthlyPlanForm = ({ onSubmit, onCancel, initialData = null, loading = false }) => {
  const { company } = useAuth();
  const [formData, setFormData] = useState({
    // Dados do plano
    name: initialData?.name || '',
    description: initialData?.description || '',
    total_value: initialData?.total_value || '', // Valor total do plano
    installments: initialData?.installments || initialData?.recurrence_months || 1, // Número de parcelas
    monthly_value: initialData?.monthly_value || '', // Calculado automaticamente
    due_day: initialData?.due_day || 10,
    
    // Integração com Asaas (sem necessidade de selecionar banco)
    
    // Configurações
    active: initialData?.active !== undefined ? initialData.active : true,
    auto_generate_invoices: initialData?.auto_generate_invoices !== undefined ? initialData.auto_generate_invoices : true,
    send_notifications: initialData?.send_notifications !== undefined ? initialData.send_notifications : true,
  });

  const [errors, setErrors] = useState({});
  const [banks, setBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(true);

  // Carregar bancos disponíveis
  useEffect(() => {
    if (company?.id) {
      loadBanks();
    }
  }, [company]);

  const loadBanks = async () => {
    try {
      setLoadingBanks(true);
      const banksData = await dataHelpers.getBanks(company.id);
      setBanks(banksData || []);
    } catch (error) {
      console.error('Error loading banks:', error);
      toast.error('Erro ao carregar bancos');
    } finally {
      setLoadingBanks(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      // Calcular valor da parcela automaticamente
      if (name === 'total_value' || name === 'installments') {
        const totalValue = parseFloat(name === 'total_value' ? value : newFormData.total_value) || 0;
        const installments = parseInt(name === 'installments' ? value : newFormData.installments) || 1;
        
        if (totalValue > 0 && installments > 0) {
          newFormData.monthly_value = (totalValue / installments).toFixed(2);
        }
      }
      
      return newFormData;
    });

    // Limpar erro do campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validações obrigatórias
    if (!formData.name.trim()) {
      newErrors.name = 'Nome do plano é obrigatório';
    }

    if (!formData.total_value || parseFloat(formData.total_value) <= 0) {
      newErrors.total_value = 'Valor total deve ser maior que zero';
    }

    if (!formData.installments || parseInt(formData.installments) <= 0) {
      newErrors.installments = 'Número de parcelas deve ser maior que zero';
    }

    const dueDay = parseInt(formData.due_day);
    if (dueDay < 1 || dueDay > 31) {
      newErrors.due_day = 'Dia de vencimento deve estar entre 1 e 31';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    try {
      // Preparar dados para envio
      const planData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        total_value: parseFloat(formData.total_value),
        installments: parseInt(formData.installments),
        monthly_value: parseFloat(formData.monthly_value),
        due_day: parseInt(formData.due_day),
        recurrence_months: parseInt(formData.installments), // Usar installments como recurrence_months
        active: formData.active,
        auto_generate_invoices: formData.auto_generate_invoices,
        send_notifications: formData.send_notifications
      };

      await onSubmit(planData);
    } catch (error) {
      console.error('Error submitting plan:', error);
      toast.error('Erro ao salvar plano');
    }
  };

  const selectedBank = banks.find(bank => bank.id === formData.bank_id);
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[98vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center">
            <CreditCard className="w-5 h-5 text-blue-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">
              {initialData ? 'Editar Plano' : 'Novo Plano'}
            </h2>
          </div>
          <button
            onClick={onCancel}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">          {/* Informações Básicas */}
          <Card className="p-4">
            <div className="flex items-center mb-3">
              <Info className="w-4 h-4 text-blue-600 mr-2" />
              <h3 className="text-base font-medium text-gray-900">Informações Básicas</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Nome do Plano *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-2 py-1.5 text-sm border rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Ex: Plano Básico, Plano Premium"
                  required
                />
                {errors.name && (
                  <p className="mt-0.5 text-xs text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Valor Total do Plano *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="total_value"
                  value={formData.total_value}
                  onChange={handleChange}
                  className={`w-full px-2 py-1.5 text-sm border rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent ${
                    errors.total_value ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="0,00"
                  required
                />
                {errors.total_value && (
                  <p className="mt-0.5 text-xs text-red-600">{errors.total_value}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Número de Parcelas *
                </label>
                <input
                  type="number"
                  min="1"
                  max="60"
                  name="installments"
                  value={formData.installments}
                  onChange={handleChange}
                  className={`w-full px-2 py-1.5 text-sm border rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent ${
                    errors.installments ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="1"
                  required
                />
                {errors.installments && (
                  <p className="mt-0.5 text-xs text-red-600">{errors.installments}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Valor da Parcela
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="monthly_value"
                  value={formData.monthly_value}
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded bg-gray-50"
                  placeholder="Calculado automaticamente"
                  readOnly
                />
                <p className="mt-0.5 text-xs text-gray-500">Calculado automaticamente</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Dia Vencimento *
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  name="due_day"
                  value={formData.due_day}
                  onChange={handleChange}
                  className={`w-full px-2 py-1.5 text-sm border rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent ${
                    errors.due_day ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.due_day && (
                  <p className="mt-0.5 text-xs text-red-600">{errors.due_day}</p>
                )}
              </div>


              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                  placeholder="Descrição opcional do plano"
                />
              </div>
            </div>
          </Card>

          {/* Integração Asaas */}
          <Card className="p-4">
            <div className="flex items-center mb-3">
              <Building2 className="w-4 h-4 text-green-600 mr-2" />
              <h3 className="text-base font-medium text-gray-900">Integração de Pagamento</h3>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                <span className="text-sm text-green-800 font-medium">Asaas configurado</span>
              </div>
              <p className="text-xs text-green-700 mt-1">
                Boletos e cobranças serão processados automaticamente via Asaas
              </p>
            </div>
          </Card>
          {/* Configura e7 f5es */}
          <Card className="p-4">
            <div className="flex items-center mb-3">
              <Settings className="w-4 h-4 text-purple-600 mr-2" />
              <h3 className="text-base font-medium text-gray-900">Configurações</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="active"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="active" className="text-sm text-gray-700">
                  Plano ativo
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="auto_generate_invoices"
                  name="auto_generate_invoices"
                  checked={formData.auto_generate_invoices}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="auto_generate_invoices" className="text-sm text-gray-700">
                  Gerar faturas automaticamente
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="send_notifications"
                  name="send_notifications"
                  checked={formData.send_notifications}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="send_notifications" className="text-sm text-gray-700">
                  Enviar notificações
                </label>
              </div>
            </div>
          </Card>

          {/* Botões */}
          <div className="flex gap-2 pt-3 border-t border-gray-200">
            <Button
              type="submit"
              variant="primary"
              icon={Save}
              loading={loading}
              className="flex-1"
            >
              {initialData ? 'Atualizar' : 'Criar Plano'}
            </Button>
            
            <Button
              type="button"
              variant="outline"
              icon={X}
              onClick={onCancel}
              className="flex-1"
            >
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MonthlyPlanForm;
